import { DynamicForm, FileRepresentation, SelectOption } from '@/components/form-builder/form-builder.types';
import { Component, inject, Input, OnInit } from '@angular/core';
import { getPlanningReviewDetailsFormObject } from './utils/planning-review-details-form-object';
import { Tabs, TabPanel, TabList, Tab } from 'primeng/tabs';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { CommonModule } from '@angular/common';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { FileService, NAMESPACE_ENUM } from '../file-list/file-list.service';
import { CurrentTasksService } from '@/pages/current-tasks/current-tasks.service';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import { PpmInspectContentBlockComponent } from '@/pages/ppm/inspect-plan/content-block/content-block.component';
import { PpmRepository } from '@/store/ppm-state/ppm-state.repository';
import { TranslateModule } from '@ngx-translate/core';
import { MeetingService } from '@/pages/meeting/meeting.service';
import { DacInspectContentBlockComponent } from '@/pages/dac/inspect-dac/content-block/content-block.component';
import { DacDetails, PpmDetails, TaskDetails } from '@/types';
import { fileUtils } from '@/utils/file.utils';
import { MeetingRequests } from '@/types/meeting.types';
import { ReportInspectContentBlockComponent } from '@/pages/report-request/report-inspect-content-block/report-inspect-content-block.component';
import { MessageService } from 'primeng/api';
import { ClarificationInspectContentBlockComponent } from '@/pages/clarification-request/clarification-inspect-content-block/clarification-inspect-content-block.component';

@Component({
  selector: 'sm-planning-review-details',
  imports: [
    Tabs,
    TabPanel,
    FormBuilderComponent,
    TabList,
    Tab,
    CommonModule,
    Dialog,
    PpmInspectContentBlockComponent,
    TranslateModule,
    DacInspectContentBlockComponent,
    ReportInspectContentBlockComponent,
    ClarificationInspectContentBlockComponent,
  ],
  templateUrl: './planning-review-details.component.html',
  styleUrl: './planning-review-details.component.scss',
})
export class PlanningReviewDetailsComponent implements OnInit {
  @Input() planningReviewDetails!: any[];
  @Input() isInspectMode!: boolean;
  meetingRepository = inject(MeetingRepository);
  ppmRepository = inject(PpmRepository);
  meetingService = inject(MeetingService);
  currentTask = inject(CurrentTasksService);
  fileService = inject(FileService);

  formObject!: DynamicForm;
  additifFormObject!: DynamicForm;

  selectedTab = this.planningReviewDetails && this.planningReviewDetails[0]?.ref;

  activeDossier: any = null;

  comVerdictOptions: SelectOption[] = [];

  visible = false;

  previewPdfSrc: BehaviorSubject<string> = new BehaviorSubject<string>('');

  ngOnInit() {
    if (this.planningReviewDetails && this.planningReviewDetails.length > 0) {
      this.selectedTab = this.planningReviewDetails[0].ref;

      forkJoin({
        taskDetails: this.getTaskDetailsByType(this.planningReviewDetails[0]),
      }).subscribe({
        next: ({ taskDetails }) => {
          this.setVerdictOptions(taskDetails as PpmDetails | DacDetails | TaskDetails | any, this.planningReviewDetails[0]);
          this.buildFormObject(this.planningReviewDetails[0], this.planningReviewDetails.indexOf(this.planningReviewDetails[0]));
        },
      });

      this.buildFormObject(this.planningReviewDetails[0], 0);
      if (this.planningReviewDetails[0].type === 'ADDITIF') this.getFile(0);
    }
  }

  onTabChange(dossier: any) {
    this.selectedTab = dossier;
    const selectedDossier = this.planningReviewDetails.find(d => d.ref === dossier);

    if (selectedDossier) {
      this.selectedTab = selectedDossier.ref;
      forkJoin({
        taskDetails: this.getTaskDetailsByType(selectedDossier),
      }).subscribe(({ taskDetails }) => {
        this.setVerdictOptions(taskDetails as PpmDetails | DacDetails | TaskDetails | any, selectedDossier);
        this.buildFormObject(selectedDossier, this.planningReviewDetails.indexOf(selectedDossier));
      });
      if (selectedDossier.type === 'ADDITIF') this.getFile(this.planningReviewDetails.indexOf(selectedDossier));

    }
  }

  private getTaskDetailsByType(dossier: any) {
    switch (dossier.type) {
      case 'PPM':

        return this.currentTask.getTaskDetailsPpm(dossier.taskId);
      case 'DAC':

        return this.meetingService.getDacTasks(dossier.taskId);
      case 'ADDITIF':

        return this.meetingService.getRequestsTasks(dossier.taskId);

      case 'REPORT':
        return this.meetingService.getReportTasks(dossier.taskId);

      default:
        return this.currentTask.getTaskDetailsPpm(dossier.taskId);
    }
  }

  private setVerdictOptions(taskDetails: PpmDetails | DacDetails | TaskDetails, dossier: any) {
    if (taskDetails.nextSteps) {
      const comType = this.meetingRepository.getNested<string>('response', 'com', 'comType', 'code');
      this.comVerdictOptions = taskDetails.nextSteps.map(step => {
        return {
          code: String(step.validationValue),
          name: dossier.type === 'DAC' && comType === 'CPMP' ? step.actionLabel : `meeting.planning.reviewDetails.form.comitteeVerdict.${step.validationValue}`,
        };
      });
    } else {
      this.comVerdictOptions = [
        {
          code: dossier.decision,
          name: `meeting.planning.reviewDetails.form.comitteeVerdict.${dossier.decision}`,
        },
      ];
    }
  }

  private buildFormObject(dossier: any, index: number) {
    const namespace =
      dossier.type === 'PPM'
        ? NAMESPACE_ENUM.MEETING_PPM_FOR_MEETING
        : dossier.type === 'DAC'
          ? NAMESPACE_ENUM.MEETING_DAC_FOR_MEETING
          :dossier.type=="REPORT" ?
            NAMESPACE_ENUM.MEETING_REPORT_FOR_MEETING:
            NAMESPACE_ENUM.MEETING_REQUESTS;
    this.formObject = getPlanningReviewDetailsFormObject({
      planningReviewDetails: dossier,
      store: this.meetingRepository,
      comVerdictOptions: this.comVerdictOptions,
      index,
      download: (file: FileRepresentation) => {
        this.fileService.download(file, namespace);
      },
      openFile: (file: FileRepresentation) => this.fileService.openFile(file, namespace),
      isInspectMode: this.isInspectMode,
      viewDetails: () => {
        this.activeDossier = dossier;
        this.visible = true;
      },
    });
  }

  downloadSignedPpm() {
    this.fileService.download(
      {
        filename: this.ppmRepository.getNested('response', 'ref'),
        extension: 'pdf',
        alfresco_id: this.ppmRepository.getNested('response', 'modelAlfrescoId'),
      },
      NAMESPACE_ENUM.PPM_MODEL
    );
  }

  private getFile(index: number) {
    const dossier = this.meetingRepository.getNested<MeetingRequests[]>('response', 'reunionDemandes')[index].demande;
    this.fileService
      .getUrlFromAlfrescoObject(
        {
          ...dossier.doc,
        },
        NAMESPACE_ENUM.REQUESTS
      )
      .subscribe(blobData => {
        this.previewPdfSrc.next(
          URL.createObjectURL(
            new Blob([blobData as BlobPart], {
              type: fileUtils.getMimeType(dossier.doc.fileExtension) || 'pdf',
            })
          )
        );
      });
  }
}
